import { useEffect, useState } from 'react';
import { Loader2, Play } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useCustomers } from '../hooks/useCustomers.ts';
import { useCallInCadenceReport } from '../hooks/useCallInCadenceReport.ts';
import { CallInCadenceTable } from '../components/CallInCadenceTable.tsx';
import { CustomerPicker } from '../components/CustomerPicker.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx';
import {
  CALL_IN_CADENCE_WINDOW_DAYS,
  type CallInCadenceWindowDays,
  DEFAULT_CALL_IN_CADENCE_WINDOW_DAYS,
} from '@metron/shared';

function parseWindowDays(raw: string | null): CallInCadenceWindowDays {
  if (raw === null) return DEFAULT_CALL_IN_CADENCE_WINDOW_DAYS;
  const n = Number(raw);
  return (CALL_IN_CADENCE_WINDOW_DAYS as readonly number[]).includes(n)
    ? (n as CallInCadenceWindowDays)
    : DEFAULT_CALL_IN_CADENCE_WINDOW_DAYS;
}

function parseCustomerId(raw: string | null): number | undefined {
  if (raw === null) return undefined;
  const n = parseInt(raw, 10);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

function formatWindowDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function toCustomerSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export function CallInCadenceReportPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL state: drives the query. Only updated by the Run report button.
  const committedCustomerId = parseCustomerId(searchParams.get('customerId'));
  const committedWindowDays = parseWindowDays(searchParams.get('windowDays'));

  // Local state: what the form is showing. Seeded from URL on mount and
  // resynced when URL changes externally (e.g. browser back/forward).
  const [pendingCustomerId, setPendingCustomerId] = useState<number | undefined>(
    committedCustomerId,
  );
  const [pendingWindowDays, setPendingWindowDays] = useState<CallInCadenceWindowDays>(
    committedWindowDays,
  );

  // Resync pending → committed when URL changes from outside (back button, link).
  useEffect(() => {
    setPendingCustomerId(committedCustomerId);
    setPendingWindowDays(committedWindowDays);
  }, [committedCustomerId, committedWindowDays]);

  const { customers, isLoading: customersLoading } = useCustomers();
  const {
    data: report,
    isLoading: reportLoading,
    error: reportError,
  } = useCallInCadenceReport(committedCustomerId, committedWindowDays);

  const pendingCustomer = customers.find((c) => c.id === pendingCustomerId);
  const reportCustomer = customers.find((c) => c.id === committedCustomerId);
  const customerSlug = reportCustomer
    ? toCustomerSlug(reportCustomer.waterscopeAccountName)
    : 'unknown';

  const handlePickCustomer = (customerId: number) => {
    setPendingCustomerId(customerId);
  };

  const handleClearForm = () => {
    setPendingCustomerId(undefined);
    setPendingWindowDays(DEFAULT_CALL_IN_CADENCE_WINDOW_DAYS);
  };

  const handleRunReport = () => {
    if (pendingCustomerId === undefined) return;
    const next: Record<string, string> = { customerId: String(pendingCustomerId) };
    if (pendingWindowDays !== DEFAULT_CALL_IN_CADENCE_WINDOW_DAYS) {
      next.windowDays = String(pendingWindowDays);
    }
    setSearchParams(next);
  };

  // Disable Run when there's nothing to run, when the form already matches
  // what's loaded, or when a query is in flight.
  const isDirty = pendingCustomerId !== committedCustomerId ||
    pendingWindowDays !== committedWindowDays;
  const canRun = pendingCustomerId !== undefined && (isDirty || !report) && !reportLoading;

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-3xl font-bold'>Call-in Cadence</h1>
        <p className='text-muted-foreground mt-1'>
          Per-meter call-in frequency for a selected customer.
        </p>
      </div>

      <div className='space-y-2'>
        <div className='flex items-end gap-2 flex-wrap'>
          <div className='flex-1 min-w-[280px] max-w-xl'>
            <label htmlFor='customer-search' className='text-sm font-medium'>
              Customer
            </label>
            {
              /* Bind the picker to the pending id so its internal "Selected:" line
                still appears after Run report — committed and pending stay in
                sync once a report is loaded. */
            }
            <CustomerPicker
              customers={customers}
              selectedCustomerId={pendingCustomerId}
              onSelect={handlePickCustomer}
              isLoading={customersLoading}
              placeholder='Search by name, ID, or account type…'
              inputId='customer-search'
            />
          </div>
          <div className='space-y-1'>
            <label className='text-sm font-medium'>Window</label>
            <Select
              value={String(pendingWindowDays)}
              onValueChange={(value) =>
                setPendingWindowDays(Number(value) as CallInCadenceWindowDays)}
            >
              <SelectTrigger className='w-[140px]' aria-label='Window in days'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CALL_IN_CADENCE_WINDOW_DAYS.map((days) => (
                  <SelectItem key={days} value={String(days)}>
                    {days} days
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleRunReport} disabled={!canRun}>
            <Play className='h-4 w-4 mr-2' />
            Run report
          </Button>
          {(pendingCustomer || committedCustomerId !== undefined) && (
            <Button variant='ghost' onClick={handleClearForm}>
              Clear
            </Button>
          )}
        </div>

        {pendingCustomer && isDirty && committedCustomerId !== undefined && (
          <p className='text-sm text-amber-600'>
            Selected:{' '}
            <span className='font-medium'>
              {pendingCustomer.waterscopeAccountName}
            </span>{' '}
            — click Run report to load.
          </p>
        )}
      </div>

      {committedCustomerId === undefined && pendingCustomerId === undefined && (
        <div className='py-12 text-center text-muted-foreground'>
          <p>Search for a customer, choose a window, then click Run report.</p>
        </div>
      )}

      {committedCustomerId !== undefined && reportLoading && (
        <div className='flex items-center justify-center py-12'>
          <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          <span className='ml-2 text-muted-foreground'>Loading report…</span>
        </div>
      )}

      {committedCustomerId !== undefined && reportError && (
        <div className='py-12 text-center text-destructive'>
          <p>Failed to load report. Please try again.</p>
        </div>
      )}

      {report && (
        <div className='space-y-2'>
          <p className='text-sm text-muted-foreground'>
            Window: {formatWindowDate(report.windowStart)} → {formatWindowDate(report.windowEnd)}
            {' '}
            ({committedWindowDays} days)
          </p>
          <CallInCadenceTable
            rows={report.rows}
            customerSlug={customerSlug}
            windowEnd={report.windowEnd}
          />
        </div>
      )}
    </div>
  );
}
